import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Search, Download } from "lucide-react";
import { toast } from "sonner";
import Modal from "./Modal";
import GalleryUploadField from "./GalleryUploadField";
import ImageUploadField from "./ImageUploadField";
import { Spinner, EmptyState } from "@/components/common/Primitives";
import { downloadCsv } from "@/lib/exportCsv";
import { collectR2UrlsFromRow, findRemovedR2Urls } from "@/lib/r2Media";
import { storageService } from "@/services/storageService";
import { invalidateActiveForms, invalidateUnitAggregates } from "@/lib/invalidateQueries";

/**
 * Generic admin CRUD module.
 *
 * props:
 *  - title, description
 *  - crud: { list, create, update, remove } (from crudFor)
 *  - columns: [{ key, label, render?(row) }]
 *  - fields:  [{ name, label, type, options?, required?, placeholder?, hint? }]
 *            types: text | textarea | number | date | select | checkbox | json | gallery | image
 *  - searchKeys: string[]
 *  - toggles: [{ key, label }]  → quick on/off switches rendered in the table
 *  - exportCsv: { filename, columns: [{ key, label, value?(row) }] }  → optional CSV export
 */
export default function CrudModule({
  title,
  description,
  crud,
  columns,
  fields,
  searchKeys = [],
  toggles = [],
  exportCsv,
  onRowsChange,
}) {
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const data = await crud.list();
    setRows(data);
    onRowsChange?.(data);
  }, [crud, onRowsChange]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(q))
    );
  }, [rows, search, searchKeys]);

  function openCreate() {
    setEditing(null);
    const init = {};
    fields.forEach((f) => {
      if (f.type === "checkbox") init[f.name] = f.default ?? true;
      else if (f.type === "gallery") init[f.name] = f.default ?? [];
      else if (f.default !== undefined) init[f.name] = f.default;
    });
    setValues(init);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditing(row);
    const init = {};
    fields.forEach((f) => {
      if (f.type === "json") init[f.name] = JSON.stringify(row[f.name] ?? null, null, 2);
      else if (f.type === "gallery") init[f.name] = Array.isArray(row[f.name]) ? row[f.name] : [];
      else init[f.name] = row[f.name] ?? "";
    });
    setValues(init);
    setModalOpen(true);
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      fields.forEach((f) => {
        let v = values[f.name];
        if (f.type === "number") v = v === "" || v === null ? null : Number(v);
        if (f.type === "json") {
          try {
            v = v ? JSON.parse(v) : null;
          } catch {
            throw new Error(`"${f.label}" contains invalid JSON`);
          }
        }
        if (f.type === "gallery") {
          v = Array.isArray(v) ? v.filter(Boolean) : [];
        }
        payload[f.name] = v === "" ? null : v;
      });
      if (editing) {
        const removedUrls = findRemovedR2Urls(editing, { ...editing, ...payload }, fields);
        if (removedUrls.length) {
          await storageService.deleteR2Urls(removedUrls);
        }
        await crud.update(editing.id, payload);
        toast.success(`${title.replace(/s$/, "")} updated`);
      } else {
        await crud.create(payload);
        toast.success(`${title.replace(/s$/, "")} created`);
      }
      setModalOpen(false);
      await load();
      if (crud.table === "units") {
        await invalidateUnitAggregates();
      }
      if (crud.table === "forms") {
        await invalidateActiveForms();
      }
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function remove(row) {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this record? This cannot be undone.")) return;
    try {
      const mediaUrls = collectR2UrlsFromRow(row, fields);
      let storageWarning = null;
      if (mediaUrls.length) {
        try {
          await storageService.deleteR2Urls(mediaUrls);
        } catch (err) {
          storageWarning = err.message || "Could not remove all files from storage";
        }
      }
      await crud.remove(row.id);
      if (storageWarning) {
        toast.warning(`Record deleted, but ${storageWarning.toLowerCase()}`);
      } else {
        toast.success("Deleted");
      }
      await load();
      if (crud.table === "units") {
        await invalidateUnitAggregates();
      }
      if (crud.table === "forms") {
        await invalidateActiveForms();
      }
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  }

  async function toggle(row, key) {
    try {
      await crud.update(row.id, { [key]: !row[key] });
      await load();
      if (crud.table === "units") {
        await invalidateUnitAggregates();
      }
      if (crud.table === "forms") {
        await invalidateActiveForms();
      }
    } catch (err) {
      toast.error(err.message || "Update failed");
    }
  }

  const moduleId = title.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  function handleExportCsv() {
    if (!exportCsv || !filtered?.length) {
      toast.error("No records to export");
      return;
    }
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv({
      filename: `${exportCsv.filename}-${date}`,
      columns: exportCsv.columns,
      rows: filtered,
    });
    toast.success(`Exported ${filtered.length} record${filtered.length === 1 ? "" : "s"}`);
  }

  return (
    <div data-testid={`admin-module-${moduleId}`}>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-ink">{title}</h1>
          {description && <p className="mt-1 text-sm text-ink-muted max-w-2xl">{description}</p>}
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {exportCsv && (
            <button
              type="button"
              className="btn-outline shrink-0"
              onClick={handleExportCsv}
              disabled={!filtered?.length}
              data-testid={`export-${moduleId}-csv-btn`}
            >
              <Download size={16} /> Export CSV
            </button>
          )}
          <button className="btn-primary shrink-0" onClick={openCreate} data-testid={`add-${moduleId}-btn`}>
            <Plus size={16} /> Add new
          </button>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-xl border border-gray-200 shadow-card overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3">
          <Search size={16} className="text-ink-muted" />
          <input
            className="flex-1 text-sm outline-none bg-transparent"
            placeholder={`Search ${title.toLowerCase()}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid={`search-${moduleId}-input`}
          />
          {rows && <span className="text-xs text-ink-muted">{filtered?.length ?? 0} of {rows.length}</span>}
        </div>

        {!filtered ? (
          <Spinner />
        ) : filtered.length === 0 ? (
          <EmptyState title="No records" message="Use the 'Add new' button to create one." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted bg-cream-alt/60 border-b border-gray-100">
                  {columns.map((c) => (
                    <th key={c.key} className="px-5 py-3 font-semibold">{c.label}</th>
                  ))}
                  {toggles.map((t) => (
                    <th key={t.key} className="px-3 py-3 font-semibold">{t.label}</th>
                  ))}
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.id} className="border-b border-gray-50 hover:bg-cream/70 transition-colors" data-testid={`row-${row.id}`}>
                    {columns.map((c) => (
                      <td key={c.key} className="px-5 py-3.5 align-top max-w-[280px]">
                        {c.render ? c.render(row) : <span className="line-clamp-2">{String(row[c.key] ?? "—")}</span>}
                      </td>
                    ))}
                    {toggles.map((t) => (
                      <td key={t.key} className="px-3 py-3.5 align-top">
                        <button
                          onClick={() => toggle(row, t.key)}
                          className={`relative w-9 h-5 rounded-full transition-colors ${row[t.key] ? "bg-green-600" : "bg-gray-300"}`}
                          aria-label={`Toggle ${t.label}`}
                          data-testid={`toggle-${t.key}-${row.id}`}
                        >
                          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${row[t.key] ? "left-[18px]" : "left-0.5"}`} />
                        </button>
                      </td>
                    ))}
                    <td className="px-5 py-3.5 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(row)} className="p-1.5 rounded-md text-ink-muted hover:text-maroon hover:bg-maroon-50 transition-colors" data-testid={`edit-${row.id}`}>
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => remove(row)} className="p-1.5 rounded-md text-ink-muted hover:text-red-700 hover:bg-red-50 transition-colors" data-testid={`delete-${row.id}`}>
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? `Edit ${title.replace(/s$/, "")}` : `Add ${title.replace(/s$/, "")}`} wide>
        <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fields.map((f) => (
            <div
              key={f.name}
              className={
                f.type === "textarea" || f.type === "json" || f.type === "gallery" || f.type === "image"
                  ? "sm:col-span-2"
                  : ""
              }
            >
              {f.type === "gallery" ? (
                <GalleryUploadField
                  label={f.label}
                  value={Array.isArray(values[f.name]) ? values[f.name] : []}
                  onChange={(urls) => setValues({ ...values, [f.name]: urls })}
                  nameHint={values.slug || "event"}
                  testId={`field-${f.name}`}
                />
              ) : f.type === "image" ? (
                <ImageUploadField
                  label={f.label}
                  value={values[f.name] || ""}
                  onChange={(url) => setValues({ ...values, [f.name]: url })}
                  nameHint={(f.nameHintFrom && values[f.nameHintFrom]) || f.nameHint || "image"}
                  onUpload={f.onUpload}
                  folderLabel={f.folderLabel}
                  testId={`field-${f.name}`}
                />
              ) : (
                <>
              <label className="label-bcs">
                {f.label}
                {f.required && <span className="text-maroon ml-0.5">*</span>}
              </label>
              {f.type === "textarea" ? (
                <textarea className="input-bcs min-h-[100px]" required={f.required} value={values[f.name] || ""} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} data-testid={`field-${f.name}`} />
              ) : f.type === "json" ? (
                <textarea className="input-bcs min-h-[140px] font-mono text-xs" value={values[f.name] || ""} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} data-testid={`field-${f.name}`} />
              ) : f.type === "select" ? (
                <select className="input-bcs" required={f.required} value={values[f.name] || ""} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} data-testid={`field-${f.name}`}>
                  <option value="">Select…</option>
                  {(f.options || []).map((o) => {
                    const value = typeof o === "object" ? o.value : o;
                    const label = typeof o === "object" ? o.label : o;
                    return (
                      <option key={value} value={value}>{label}</option>
                    );
                  })}
                  {values[f.name] &&
                    !(f.options || []).some((o) => (typeof o === "object" ? o.value : o) === values[f.name]) && (
                      <option value={values[f.name]}>{values[f.name]}</option>
                    )}
                </select>
              ) : f.type === "checkbox" ? (
                <label className="flex items-center gap-2 py-2 text-sm text-ink-soft cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-maroon" checked={!!values[f.name]} onChange={(e) => setValues({ ...values, [f.name]: e.target.checked })} data-testid={`field-${f.name}`} />
                  Enabled
                </label>
              ) : (
                <input type={f.type || "text"} className="input-bcs" required={f.required} placeholder={f.placeholder} value={values[f.name] ?? ""} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} data-testid={`field-${f.name}`} />
              )}
              {f.hint && <p className="mt-1 text-xs text-ink-muted">{f.hint}</p>}
                </>
              )}
            </div>
          ))}
          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" className="btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving} data-testid={`save-${moduleId}-btn`}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
